<template>
    <div
        v-if="visibleItems.length"
        :id="SELECTION_TOOLBAR_ID"
        ref="toolbarEl"
        class="selection-toolbar-wrapper"
        :style="toolbarStyle"
    >
        <template v-for="(item, idx) in visibleItems">
            <div v-if="item.isDivider" :key="`toolbar-divider-${idx}`" class="toolbar-divider" />
            <button
                v-else
                :key="`toolbar-item-${idx}`"
                v-tippy
                :content="item.title"
                class="toolbar-item"
                :title="item.title"
                @click="handleItemClick(item)"
            >
                <font-awesome-icon :icon="item.icon" />
            </button>
        </template>
        <template v-if="isMultiSelection">
            <div class="toolbar-divider" />
            <button
                v-for="mode in ALIGN_MODES"
                :key="`align-mode-${mode.value}`"
                v-tippy
                :content="mode.title"
                class="toolbar-item"
                :class="{ active: alignType === mode.value }"
                :title="mode.title"
                @click="handleAlignModeClick(mode.value)"
            >
                <font-awesome-icon :icon="mode.icon" />
            </button>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, reactive, ref, watch } from 'vue';
import { useToaster } from '@/composables/toaster';
import {
    type SelectionToolbarItem,
    type SelectionToolbar,
    SELECTION_TOOLBAR_ACTION,
    SELECTION_TOOLBAR_EVENT,
    SELECTION_TOOLBAR_ID
} from '@/angular-app/libs/fabric/fabric.selectiontoolbar';
import type { CanvasEx } from '@/typings/canvas';
import type { Object as FabricObject } from 'fabric/fabric-impl';
import { DatadogEvent, fireDatadogLog } from '@/utils/datadogUtils';

const ALIGN_MODES = [
    { value: 'selection' as const, title: 'Align to selection', icon: ['fak', 'selection'] },
    { value: 'canvas' as const, title: 'Align to workspace', icon: ['fak', 'canvas'] }
];

const props = defineProps<{
    canvas: CanvasEx;
    events?: Partial<Record<SELECTION_TOOLBAR_ACTION, (element?: FabricObject) => void>>;
    alignType?: 'selection' | 'canvas';
}>();

const emit = defineEmits<{
    (e: 'align-type-changed', alignType: 'selection' | 'canvas'): void;
}>();

/** Gap between the selection's bottom edge and the toolbar, and margin kept off the viewport edges */
const ANCHOR_GAP_PX = 12;
const VIEWPORT_MARGIN_PX = 8;

const toolbarEl = ref<HTMLElement | null>(null);

const toolbar = reactive({
    x: '0px',
    y: '0px',
    items: [] as SelectionToolbarItem[],
    element: undefined as FabricObject | undefined
});

const positioned = ref(false);
const toolbarStyle = computed(() => ({
    top: toolbar.y,
    left: toolbar.x,
    visibility: positioned.value ? 'visible' : 'hidden'
}));

const isMultiSelection = computed(() => toolbar.element?.isType('activeSelection'));

// Dividers around a fully hidden group would render as double separators
const visibleItems = computed(() => {
    const shown = toolbar.items.filter((item) => !item.hidden);
    return shown.filter(
        (item, idx) => !item.isDivider || (idx > 0 && idx < shown.length - 1 && !shown[idx - 1].isDivider)
    );
});

const toast = useToaster();

// Canvas shortcuts (undo/redo, Shift+F) listen on its event container, and clicking a button here takes the
//  focus away from it
const focusCanvas = () => {
    const containerId = props.canvas?.eventContainer;
    if (containerId) document.getElementById(containerId)?.focus({ preventScroll: true });
};

const handleItemClick = (item: SelectionToolbarItem) => {
    try {
        const callback = item.onClick || props.events?.[item.action!];
        callback?.(toolbar.element);
    } catch (error) {
        const msg = `Error executing action ${item.title}`;
        toast.error(msg);
        console.error(msg, error);
    } finally {
        fireDatadogLog(`${item.title} - Selection Toolbar` as DatadogEvent);
        focusCanvas();
    }
};

const handleAlignModeClick = (alignType: 'selection' | 'canvas') => {
    emit('align-type-changed', alignType);
    focusCanvas();
};

/**
 * The anchor is the center of the selection's bottom edge; the toolbar is centered on it and
 *  clamped so it never leaves the viewport, flipping above the selection when it would overflow
 *  the bottom.
 */
const positionToolbar = (anchorX: number, anchorY: number) => {
    const el = toolbarEl.value;
    if (!el) return;

    const { width, height } = el.getBoundingClientRect();
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN_PX;
    const left = Math.min(Math.max(anchorX - width / 2, VIEWPORT_MARGIN_PX), Math.max(maxLeft, VIEWPORT_MARGIN_PX));

    const below = anchorY + ANCHOR_GAP_PX;
    const top = below + height > window.innerHeight - VIEWPORT_MARGIN_PX ? anchorY - ANCHOR_GAP_PX - height : below;

    toolbar.x = `${left}px`;
    toolbar.y = `${Math.max(top, VIEWPORT_MARGIN_PX)}px`;
    positioned.value = true;
};

const onToolbarShown = (event: SelectionToolbar) => {
    // Only a new selection hides first: repositioning an already painted toolbar (zoom, pan, the end
    //  of a drag) would otherwise blink on every step
    if (toolbar.element !== event.selectedElement) {
        positioned.value = false;
    }
    toolbar.items = [...event.items];
    toolbar.element = event.selectedElement;
    // The width is only known once the items have rendered, and centering depends on it
    nextTick(() => positionToolbar(event.x, event.y));
};

const onToolbarHidden = () => {
    toolbar.items = [];
    toolbar.element = undefined;
};

watch(
    () => props.canvas,
    (canvas) => {
        if (!canvas) return;
        canvas.on(SELECTION_TOOLBAR_EVENT.SHOW, onToolbarShown);
        canvas.on(SELECTION_TOOLBAR_EVENT.HIDE, onToolbarHidden);
    },
    { immediate: true }
);

onUnmounted(() => {
    props.canvas?.off(SELECTION_TOOLBAR_EVENT.SHOW, onToolbarShown);
    props.canvas?.off(SELECTION_TOOLBAR_EVENT.HIDE, onToolbarHidden);
});
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.selection-toolbar-wrapper {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.3125rem;
    background-color: white;
    border: 1px solid get-color('gray-light');
    box-shadow: 0 0 1rem 0 rgba(0, 0, 0, 0.15);
    border-radius: 0.5rem;
    position: fixed;
    z-index: 9;

    .toolbar-item {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.75rem;
        height: 1.75rem;
        font-size: 0.875rem;
        color: #333;
        background-color: unset;
        border: unset;
        border-radius: 0.25rem;
        &:hover {
            background-color: get-color('gray-lightest');
        }
        &.active {
            background-color: get-color('gray-light');
        }
    }

    .toolbar-divider {
        width: 1px;
        align-self: stretch;
        margin: 0 0.1875rem;
        background-color: get-color('gray-light');
    }
}
</style>
